import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import {
  onFeatureNotIncluded,
  featureDisplayName,
  featureDescription,
} from "../lib/featureLock";

/**
 * Zeigt bei einem global abgefangenen 403 (`feature_not_included`) einen kurzen,
 * freundlichen Upgrade-Hinweis. Spiegelt die mobile `FeatureLockCard`: Statt
 * eines rohen 403-Fehlers erfährt ein Professional-Web-Nutzer, dass die gerade
 * ausgelöste Advanced-only-Funktion (z. B. PDF-Export, Fluglog, Dokumente) nicht
 * im Tarif enthalten ist, und kommt direkt zur Paketauswahl.
 *
 * Anders als die Abo-Sperre wird hier NICHT umgeleitet — der Nutzer hat ja
 * Web-Zugang und soll auf der Seite bleiben; nur die einzelne Aktion ist
 * gesperrt.
 */
export default function FeatureLockNotice() {
  const [feature, setFeature] = useState<string | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribe = onFeatureNotIncluded((featureKey) => {
      setFeature(featureKey);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setFeature(null), 7000);
    });
    return () => {
      unsubscribe();
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  if (!feature) return null;

  const name = featureDisplayName(feature);

  return (
    <div className="feature-lock-notice" role="status" aria-live="polite">
      <div
        className="feature-lock-notice-card"
        data-testid="notice-feature-not-included"
      >
        <span className="feature-lock-notice-icon">
          <Lock size={18} />
        </span>
        <div className="feature-lock-notice-text">
          <div className="feature-lock-notice-title">
            {name} nicht im Tarif enthalten
          </div>
          <div className="feature-lock-notice-sub">
            {featureDescription(feature)}
          </div>
          <div className="feature-lock-notice-hint">
            Mit einem Upgrade Ihres Pakets schalten Sie diese Funktion frei.
          </div>
          <Link
            to="/pakete"
            className="feature-lock-notice-action"
            onClick={() => setFeature(null)}
          >
            Tarife ansehen
          </Link>
        </div>
      </div>
    </div>
  );
}
